/**
 * Tailwind CSS implementation of Radio component
 * Uses Seeds radio CSS classes from radio.css
 */

import React from "react";
import type { TypeRadioProps } from "./RadioTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

const RadioTailwind = React.forwardRef<HTMLInputElement, TypeRadioProps>(
  (
    {
      id,
      value,
      name,
      label,
      checked,
      disabled = false,
      onChange,
      onFocus,
      onBlur,
      ariaLabel,
      qa = {},
      tabIndex,
      className,
      // `color` is a styled-system prop; consumers passing it route to the
      // styled-components path via needsStyledComponents, so it never reaches
      // here. Pull it out of `rest` so it is not forwarded to the DOM as an
      // invalid attribute.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      color,
      ...rest
    },
    ref
  ) => {
    // Mirror the class component's guarded handlers: onChange only fires for an
    // unchecked, enabled input; focus/blur are simple pass-throughs.
    const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
      if (!!checked || disabled) {
        return;
      }
      onChange && onChange(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
    };

    const Outer = (label ? "label" : "span") as React.ElementType;

    const outerClass = cn(
      "seeds-radio",
      { "seeds-radio-disabled": disabled },
      className
    );

    const wrapperClass = cn("seeds-radio-wrapper", {
      "seeds-radio-checked": checked === true,
      "seeds-radio-disabled": disabled,
    });

    return (
      <Outer className={outerClass} {...rest}>
        <span className={wrapperClass}>
          <input
            ref={ref}
            type="radio"
            className="seeds-radio-input"
            id={id}
            tabIndex={tabIndex}
            aria-label={ariaLabel}
            value={value}
            name={name}
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            data-qa-radio={name || ""}
            data-qa-radio-ischecked={checked === true}
            data-qa-radio-isdisabled={disabled === true}
            {...qa}
          />
        </span>
        {label && <span className="seeds-radio-label">{label}</span>}
      </Outer>
    );
  }
);

RadioTailwind.displayName = "RadioTailwind";
export default RadioTailwind;
